/**
 * 商家中心（v3.5）—— 链上推广活动（promo）全流程：
 * 入驻条件（stake ≥ merchant_min_stake）→ 创建推广活动（预算托管 PROMO_ESCROW）→
 * 活动列表 / 核销流水 → 消费者核销（五方分账：商家 85% / 推广者 8% / 返现 3% / 金库 2% / 燃烧 2%）。
 */

import { useCallback, useEffect, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { formatInt, parseDpb } from '@/lib/format';
import { MeshLinkError } from '@/lib/result';
import type { Address, PromoCreateReq, PromoRedeemReq } from '@shared/chain';

interface PromoRow {
  promo_id: string;
  from: string;
  budget: number;
  remaining: number;
  coupon_value: number;
  max_uses: number;
  used: number;
  expires_height: number;
  promoter_bps: number;
  created_height: number;
}

interface Redemption {
  promo_id: string;
  from: string;
  amount: number;
  height: number;
  ts: number;
}

const SHARE_RULE = [
  ['商家', '85%', '核销时按活动面额分账给商家'],
  ['推广者', '8%', '推荐人佣金（无推荐人归金库）'],
  ['消费者', '3%', '核销返现'],
  ['金库', '2%', '平台金库'],
  ['燃烧', '2%', '离开流通'],
] as const;

export function MerchantPage(): JSX.Element {
  const address = useIdentityStore((s) => s.address) as Address | undefined;
  // promoList 金额字段为链上 DPB 数值（非微单位），直接格式化展示
  const dpbVal = (v: number | undefined): string =>
    Number(v ?? 0).toLocaleString('zh-CN', { maximumFractionDigits: 4 });
  const [height, setHeight] = useState(0);
  const [stats, setStats] = useState<Record<string, unknown>>({});
  const [promos, setPromos] = useState<PromoRow[]>([]);
  const [reds, setReds] = useState<Redemption[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  // 创建活动表单
  const [budget, setBudget] = useState('100');
  const [coupon, setCoupon] = useState('10');
  const [uses, setUses] = useState('100');
  const [expires, setExpires] = useState('200');
  const [bps, setBps] = useState('800');
  // 消费者核销表单
  const [redeemId, setRedeemId] = useState('');
  const [redeemAmt, setRedeemAmt] = useState('1');

  const params = (stats?.params ?? {}) as Record<string, number>;
  const stakes = (stats?.stakes_detail ?? {}) as Record<string, { amount?: number }>;
  const myStake = address ? Number(stakes[address]?.amount ?? 0) : 0;
  const minStake = Number(params.merchant_min_stake ?? 100);
  const qualified = myStake >= minStake;

  const refresh = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      const [h, st, pl] = await Promise.all([
        container.chain.getBlockHeight().catch(() => 0),
        container.chain.meshStats().catch(() => ({}) as Record<string, unknown>),
        container.chain.promoList().catch(() => ({ items: [], redemptions: [] }) as Record<string, unknown>),
      ]);
      setHeight(h);
      setStats(st ?? {});
      const items = ((pl?.items ?? []) as PromoRow[]).filter((x) =>
        address ? x.from?.toLowerCase() === address.toLowerCase() : true,
      );
      setPromos(items);
      setReds((pl?.redemptions ?? []) as Redemption[]);
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(false);
    }
  }, [address]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createPromo = async (): Promise<void> => {
    if (!address) return;
    const b = parseDpb(budget);
    const c = parseDpb(coupon);
    if (b === '0' || c === '0') {
      setMsg('预算与券面额必须大于 0');
      return;
    }
    setBusy(true);
    try {
      const req: PromoCreateReq = {
        merchant: address,
        budgetMicro: b,
        couponValueMicro: c,
        maxUses: Math.max(1, Math.floor(Number(uses) || 1)),
        expiresHeight: Math.max(0, Math.floor(Number(expires) || 0)),
        promoterBps: Math.max(0, Math.min(4000, Math.floor(Number(bps) || 0))),
        signature: container.identity.signObject({ action: 'promo_create', from: address, ts: Date.now() }),
      };
      await container.chain.promoCreate(req);
      setMsg('推广活动已创建，预算已入链上托管（PROMO_ESCROW）');
      void refresh();
    } catch (err) {
      setMsg(`创建失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const redeemPromo = async (): Promise<void> => {
    if (!address) return;
    const amt = parseDpb(redeemAmt);
    if (!redeemId.trim() || amt === '0') {
      setMsg('请输入推广 ID 与核销金额');
      return;
    }
    setBusy(true);
    try {
      const req: PromoRedeemReq = {
        consumer: address,
        promoId: redeemId.trim(),
        amountMicro: amt,
        signature: container.identity.signObject({ action: 'promo_redeem', from: address, promo_id: redeemId.trim(), ts: Date.now() }),
      };
      await container.chain.promoRedeem(req);
      setMsg('核销成功（五方分账已执行，商家/推广/返现已到账）');
      void refresh();
    } catch (err) {
      setMsg(`核销失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const mineReds = reds.filter((r) =>
    promos.some((p) => p.promo_id === r.promo_id),
  );

  const inputCls =
    'w-full rounded-btn bg-panel-2 border border-line px-3 py-2 text-sm text-text placeholder-dim outline-none focus:border-accent';
  const labelCls = 'text-xs text-dim';

  return (
    <div className="space-y-4">
      {/* 入驻与规则 */}
      <section className="card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-text">商家中心</h2>
          <Badge tone="ok">链高度 {formatInt(height)}</Badge>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-dim">
          链上推广（promo）机制：商家质押保证金后创建推广活动，预算进入链上托管；
          消费者核销时按五方分账，全程上链、可审计。
        </p>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{myStake.toFixed(2)}</div>
            <div className="text-[11px] text-dim">我的质押 DPB</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{minStake.toFixed(0)}</div>
            <div className="text-[11px] text-dim">商家门槛 DPB</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{promos.length}</div>
            <div className="text-[11px] text-dim">我的活动</div>
          </div>
        </div>
        <div className="mt-3">
          {address ? (
            qualified ? (
              <Badge tone="ok">已具备商家资格（质押满足门槛）</Badge>
            ) : (
              <Badge tone="warn">未达商家门槛：请先在交易/质押入口质押 ≥ {minStake.toFixed(0)} DPB</Badge>
            )
          ) : (
            <Badge tone="warn">未连接身份</Badge>
          )}
        </div>
        <div className="mt-4">
          <div className="mb-1 text-xs font-medium text-accent">核销五方分账（链上固定）</div>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-5">
            {SHARE_RULE.map(([k, v, d]) => (
              <div key={k} className="rounded-btn bg-panel-2 px-3 py-2">
                <div className="text-xs font-medium text-text">{k} {v}</div>
                <div className="mt-0.5 text-[10px] leading-tight text-dim">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 创建推广活动 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">创建推广活动</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <label className="space-y-1">
            <span className={labelCls}>预算 (DPB)</span>
            <input className={inputCls} type="number" min="0.000001" step="any" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="如 100" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>券面额 (DPB/次)</span>
            <input className={inputCls} type="number" min="0.000001" step="any" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="如 10" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>核销次数上限</span>
            <input className={inputCls} type="number" min="1" value={uses} onChange={(e) => setUses(e.target.value)} placeholder="如 100" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>有效期（相对当前高度 +N 块）</span>
            <input className={inputCls} type="number" min="1" value={expires} onChange={(e) => setExpires(e.target.value)} placeholder="如 200" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>推广分成 (bps，≤4000=40%)</span>
            <input className={inputCls} type="number" min="0" max="4000" value={bps} onChange={(e) => setBps(e.target.value)} placeholder="如 800" />
          </label>
          <div className="flex items-end">
            <Button className="w-full" loading={busy} disabled={!address || !qualified} onClick={() => void createPromo()}>
              创建并托管
            </Button>
          </div>
        </div>
        {msg ? <p className="mt-3 text-xs text-accent">{msg}</p> : null}
      </section>

      {/* 我的推广活动 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">我的推广活动（{promos.length}）</h2>
        {promos.length === 0 ? (
          <EmptyState title="还没有推广活动" description="创建活动后，预算进入链上托管并生成推广 ID（卡券码）" />
        ) : (
          <div className="space-y-2">
            {promos.map((p) => {
              const alive = height <= p.expires_height && p.used < p.max_uses;
              return (
                <div key={p.promo_id} className="rounded-btn bg-panel-2 px-3 py-2.5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-mono text-xs text-text">#{p.promo_id.slice(0, 16)}</div>
                    {alive ? <Badge tone="ok">进行中</Badge> : <Badge tone="warn">已结束</Badge>}
                  </div>
                  <div className="mt-1 grid grid-cols-3 gap-1 text-[11px] text-dim sm:grid-cols-6">
                    <span>预算 {dpbVal(p.budget)}</span>
                    <span>券值 {dpbVal(p.coupon_value)}</span>
                    <span>核销 {p.used}/{p.max_uses}</span>
                    <span>剩余 {dpbVal(p.remaining)}</span>
                    <span>有效期至 {formatInt(p.expires_height)}</span>
                    <span>推广 {((p.promoter_bps ?? 0) / 100).toFixed(1)}%</span>
                  </div>
                  <div className="mt-1 font-mono text-[10px] text-dim">推广码（消费者核销用）：{p.promo_id}</div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* 消费者核销 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">消费核销</h2>
        <p className="mb-3 text-xs text-dim">输入推广码（promo_id）与消费金额，核销后五方分账自动执行，消费者获得返现。</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="space-y-1">
            <span className={labelCls}>推广码 promo_id</span>
            <input className={inputCls} value={redeemId} onChange={(e) => setRedeemId(e.target.value)} placeholder="16 位 hex 推广码" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>核销金额 (DPB)</span>
            <input className={inputCls} type="number" min="0.000001" step="any" value={redeemAmt} onChange={(e) => setRedeemAmt(e.target.value)} placeholder="如 1" />
          </label>
          <div className="flex items-end">
            <Button className="w-full" variant="outline" loading={busy} disabled={!address} onClick={() => void redeemPromo()}>
              核销
            </Button>
          </div>
        </div>
      </section>

      {/* 核销流水 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">相关核销流水（{mineReds.length}）</h2>
        {mineReds.length === 0 ? (
          <EmptyState title="暂无核销记录" description="消费者核销后，分账流水将在此展示" />
        ) : (
          <div className="space-y-2">
            {mineReds.slice().reverse().map((r, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-btn bg-panel-2 px-3 py-2 text-xs">
                <span className="font-mono text-text">#{r.promo_id.slice(0, 16)}</span>
                <span className="text-dim">{dpbVal(r.amount)} DPB</span>
                <span className="font-mono text-dim">{String(r.from ?? '').slice(0, 12)}…</span>
                <span className="text-dim">高度 {formatInt(r.height ?? 0)}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default MerchantPage;
